import { useEffect, useState } from "react";
import { Modal, Button, Spinner, ListGroup } from "react-bootstrap";
import { JsonView, allExpanded, defaultStyles } from "react-json-view-lite";
import "react-json-view-lite/dist/index.css";
import { NetworkTemplateService } from "@services/contentNetwork/networkTemplates/networkTemplateService";
import { LaunchNetworkService } from "@services/engagements/launch/launchNetworkService";
import type { NetworkTemplate } from "@models/networkTemplate.model";

interface NetworkTemplatePickerModalProps {
  show: boolean;
  onClose: () => void;
  network: string;
  launchId: number;
  currentTemplateId: number | null;
  onTemplateSelected: (networkTemplateId: number) => void;
}

export const NetworkTemplatePickerModal = ({
  show,
  onClose,
  network,
  launchId,
  currentTemplateId,
  onTemplateSelected,
}: NetworkTemplatePickerModalProps) => {
  const [templates, setTemplates] = useState<NetworkTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<number | null>(currentTemplateId);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    new NetworkTemplateService()
      .list(network)
      .then(setTemplates)
      .catch((err) => console.error("Failed to load network templates:", err))
      .finally(() => setLoading(false));
  }, [network]);

  const handleConfirm = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      await new LaunchNetworkService().selectTemplate(launchId, network, selectedId);
      onTemplateSelected(selectedId);
      onClose();
    } catch (err) {
      console.error("Failed to attach network template:", err);
    } finally {
      setSaving(false);
    }
  };

  const selected = templates.find((template) => template.id === selectedId) ?? null;

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Select Network Template for {network}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div className="d-flex justify-content-center py-4">
            <Spinner animation="border" />
          </div>
        ) : templates.length === 0 ? (
          <p className="text-muted mb-0">No network templates available for this network.</p>
        ) : (
          <div className="row g-3">
            <div className="col-5" style={{ maxHeight: "60vh", overflowY: "auto" }}>
              <ListGroup>
                {templates.map((template) => (
                  <ListGroup.Item
                    key={template.id}
                    action
                    active={template.id === selectedId}
                    onClick={() => setSelectedId(template.id)}
                  >
                    {template.name}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </div>

            <div className="col-7" style={{ maxHeight: "60vh", overflowY: "auto" }}>
              {selected ? (
                <>
                  <div className="mb-2">
                    <div className="fw-semibold">{selected.name}</div>
                    <small className="text-muted">
                      <strong>Network:</strong> {selected.network} &nbsp;·&nbsp;
                      <strong>ID:</strong> {selected.id}
                    </small>
                  </div>
                  <div className="border rounded p-2 bg-white">
                    <JsonView
                      data={selected.data ?? {}}
                      style={defaultStyles}
                      shouldExpandNode={allExpanded}
                    />
                  </div>
                </>
              ) : (
                <p className="text-muted mb-0">Select a template to see its data.</p>
              )}
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleConfirm} disabled={!selectedId || saving}>
          {saving ? <Spinner size="sm" animation="border" /> : "Attach Template"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
